// PenConfig — purely the local UI's idea of what the next stroke will look like.
//
// The wire-level pen kind set (`brush | flat | eraser`) lives in
// `@oekaki/protocol` (consult 2026-05-09 ペン統合: 旧 `'normal' | 'water' |
// 'alpha' | 'eraser'` から縮減). PenConfig wraps that with UI affordances
// (selected color slot index, current pen width, mask configuration) so
// reducers don't need to spread loose primitives across props.
//
// (architecture.md §16 / §19.3)

import type { PenParams, StrokeMask, StrokePen, TexturePreset } from '@oekaki/protocol';

/** UI-side pen settings. Shape kept stable so DrawingEngine adoption is trivial. */
export interface PenConfig {
  readonly pen: StrokePen;
  /** RGB24 (0..0xFFFFFF). Eraser ignores this at render time. */
  readonly color: number;
  /** Stroke alpha 0..255. */
  readonly alpha: number;
  /** Width in CSS px (clamped to MIN_STROKE_WIDTH..MAX_STROKE_WIDTH on send). */
  readonly width: number;
  /** Mask configuration. `'none'` = full canvas. */
  readonly mask: StrokeMask;
  /** MVP: always 0 (β: layer index). architecture.md §19.10 A. */
  readonly layerId: number;
  /**
   * Anti-aliased stamping (modern-layout.md §9.9). `false` = 旧 Java 版踏襲の
   * 二値スタンプ (Classic 懐古ピクセル正確、wire byte-identical) / `true` =
   * coverage AA パス。2026-06-13 (表現パリティ §1.1) で tri-state 化:
   * `useRoomChrome` が `aaPreference` の実効値 (明示設定 ?? 共通既定 off、
   * consult 2026-06-13 続報でレイアウト別既定を撤廃) を同期する。両レイアウトとも
   * 上部バー「ペン」→ PenSettingsModal から opt-in 可。
   * per-stroke で wire (`stroke.start.aa`) に乗り、同室の Classic/Modern ユーザが
   * 各自の見た目で共存する。
   */
  readonly aa: boolean;
  /**
   * Texture brush preset (texture-brush-design.md §10.1 / §11 step 3). Only
   * meaningful when `pen === 'texture'`: the Modern preset picker (紙/毛先/
   * スプレー/雲/カケアミ) writes the selected `TexturePreset` here. Other pens
   * ignore it. Always set (default `'paper'`) so `penParamsFor` can read it
   * unconditionally; it only rides the wire (`stroke.start.penParams`) while the
   * active pen is `'texture'`. Classic never surfaces texture so it stays
   * `'paper'` there (no behaviour change). (consult 2026-06-19, design §10.1)
   */
  readonly texturePreset: TexturePreset;
  /**
   * Screentone density (番手) for `pen === 'tone'` (texture-brush-design.md
   * §12.2 / consult 2026-06-20 段階1). 0..1 continuous scalar: 0 = empty,
   * 1 = full ink. The Modern「トーン」category's density slider writes this.
   * Other pens ignore it. Always set (default `DEFAULT_TONE_DENSITY` = 0.5)
   * so `penParamsFor` can read it unconditionally; it only rides the wire
   * (`stroke.start.penParams`) while the active pen is `'tone'`. Classic never
   * surfaces tone so it stays the default there (no behaviour change).
   */
  readonly toneDensity: number;
  /**
   * Airbrush cell-quantization shift (grain size, 0..3) for `pen === 'airbrush'`
   * (airbrush-design-2026-08-15.md §1.3 / §4-2 / §6). The shared `PenKindPicker`
   * (Classic PenPopup + Modern ToolProperties, 表現パリティ §0) writes this via
   * its grain slider, shown only while `pen==='airbrush'`. Other pens ignore it.
   * Always set (default `DEFAULT_AIRBRUSH_GRAIN` = 1, matches the drawing-core
   * kernel default `AIRBRUSH_DEFAULT_GRAIN`) so `penParamsFor` can read it
   * unconditionally; it only rides the wire while the active pen is `'airbrush'`
   * (design §4-2: UI-adjustable params must ride the wire so replay/late-join
   * stay reproducible).
   */
  readonly airbrushGrain: number;
  /**
   * Airbrush spray density (0..1) for `pen === 'airbrush'` (design §1.3 / §4-2
   * / §6). Same wiring as `airbrushGrain` (shared `PenKindPicker` density
   * slider). Always set (default `DEFAULT_AIRBRUSH_DENSITY` = 0.5, matches the
   * drawing-core kernel default `AIRBRUSH_DEFAULT_DENSITY`).
   */
  readonly airbrushDensity: number;
  /**
   * Per-stroke airbrush seed (u32, design §1 / §4-4 / §5-3). **NOT a persisted
   * user setting** — unlike `airbrushGrain`/`airbrushDensity`, this field only
   * exists so a single `crypto`-random draw can thread through the SAME
   * `PenConfig` object read by both the local `engine.startStroke` call and
   * `sendStrokeStart` (`DrawingCanvas.beginStrokeNow` composes a fresh
   * `effectivePen` with a new `generateAirbrushSeed()` result right before both
   * calls read it via `penParamsFor`). If `penParamsFor` generated the seed
   * itself as a side effect, calling it twice per stroke (once for the local
   * engine, once for the wire send) would draw two DIFFERENT random seeds and
   * the artist's own canvas would silently diverge from what peers /
   * server-snapshot replay draw (design §1.1 determinism invariant). Default
   * `0` is never actually sent — every airbrush stroke overwrites it in
   * `beginStrokeNow` before use.
   */
  readonly airbrushSeed: number;
  /**
   * Crayon wax-grain coarseness (0..2, phase11-light-pens-2026-08-21.md §2.6)
   * for `pen === 'crayon'`. Shared `PenParamsSection` writes this via its
   * segmented "目の粗さ" picker, shown only while `pen==='crayon'`. Other pens
   * ignore it. Always set (default `DEFAULT_CRAYON_GRAIN` = 0, 細 = 最も細かい粒,
   * matches the drawing-core kernel default `CRAYON_DEFAULT_GRAIN`) so
   * `penParamsFor` can read it unconditionally; it only rides the wire while
   * the active pen is `'crayon'` (same wiring as `airbrushGrain`, but no
   * `density` axis — crayon's threshold seam has no separate density knob).
   */
  readonly crayonGrain: number;
  /**
   * Pastel fine-powder-grain coarseness (0..2, same doc §2.6) for
   * `pen === 'pastel'`. Same wiring as `crayonGrain` (shared `PenParamsSection`
   * segmented picker). Always set (default `DEFAULT_PASTEL_GRAIN` = 1, matches
   * the drawing-core kernel default `PASTEL_DEFAULT_GRAIN`).
   */
  readonly pastelGrain: number;
  /**
   * Screentone dot-grid pitch (world px, seamless-tiling divisor of 256) for
   * `pen === 'tone'` (phase11-light-pens-2026-08-21.md §4.5-§4.7). Independent
   * axis from `toneDensity` (= dot spacing vs dot size, §4.2). UI restricts the
   * choice to `8 | 16 | 32 | 64` (`4` renders as noise, not a legible dot
   * pattern — §4.3/§4.4). Always set (default `DEFAULT_TONE_PITCH` = 16, the
   * UI default per §4.5 "既定は16以上"; NOT the same as the wire-omitted-field
   * replay fallback `TONE_DEFAULT_PITCH` = 4 on the drawing-core render side,
   * which only exists for pre-pitch recorded strokes) so `penParamsFor` can
   * read it unconditionally; it only rides the wire while the active pen is
   * `'tone'`. `pitch` is optional on the wire schema, but this function always
   * includes the current UI value (§4.6: "UI は常に pitch を載せる").
   */
  readonly tonePitch: number;
  /**
   * Dot-pen spacing (gap between stamped dots, in sweep-step units — NOT px,
   * phase11-light-pens-2026-08-21.md §3.4/§3.5) for `pen === 'dot'`. Shared
   * `PenParamsSection` writes this via its "間隔" slider (1..200, matching the
   * protocol's `dot` arm value domain), shown only while `pen==='dot'`. Other
   * pens ignore it. Always set (default `DEFAULT_DOT_SPACING` = 8, matches the
   * drawing-core kernel default `DOT_DEFAULT_SPACING`) so `penParamsFor` can
   * read it unconditionally. Unlike `toneDensity`/`crayonGrain`/etc, the
   * protocol's `dot` arm has `spacing` as a REQUIRED field (no optional
   * fallback) — `penParamsFor` must always include it whenever `pen==='dot'`.
   */
  readonly dotSpacing: number;
}

/** Default screentone density (番手) — matches the drawing-core kernel default. */
export const DEFAULT_TONE_DENSITY = 0.5;

/**
 * Default screentone dot-grid pitch (間隔) offered by the UI (phase11-light-
 * pens-2026-08-21.md §4.5 "既定は16以上"). Deliberately NOT the drawing-core
 * kernel's wire-omitted-field replay fallback (`TONE_DEFAULT_PITCH` = 4,
 * `stamp/texture/toneThreshold.ts`) — that constant exists only so strokes
 * recorded before this field existed keep replaying byte-identically. A fresh
 * UI selection always sends an explicit pitch, so it should default to a
 * value that reads as a legible dot pattern (`4`/`8` read as noise, §4.3/§4.4).
 */
export const DEFAULT_TONE_PITCH = 16;

/**
 * Default dot-pen spacing (間隔, sweep-step units) — mirrors the drawing-core
 * kernel default `DOT_DEFAULT_SPACING` (`@oekaki/drawing-core`
 * `stamp/dotDecorator.ts`, phase11-light-pens-2026-08-21.md §3.4/§3.5).
 */
export const DEFAULT_DOT_SPACING = 8;

/**
 * Airbrush grain/density defaults — mirror the drawing-core kernel defaults
 * `AIRBRUSH_DEFAULT_GRAIN` / `AIRBRUSH_DEFAULT_DENSITY`
 * (`@oekaki/drawing-core` `stamp/texture/airbrushHash.ts`, airbrush-design-
 * 2026-08-15.md §1.3). Duplicated here (not imported) because neither constant
 * is re-exported from the package's public `index.ts` — same precedent as
 * `DEFAULT_TONE_DENSITY` above.
 */
export const DEFAULT_AIRBRUSH_GRAIN = 1;
export const DEFAULT_AIRBRUSH_DENSITY = 0.5;

/**
 * Crayon/pastel grain defaults — mirror the drawing-core kernel defaults
 * `CRAYON_DEFAULT_GRAIN` / `PASTEL_DEFAULT_GRAIN` (`@oekaki/drawing-core`
 * `stamp/texture/crayonThreshold.ts` / `pastelTexture.ts`,
 * phase11-light-pens-2026-08-21.md §2.6). Duplicated here (not imported) —
 * same precedent as `DEFAULT_AIRBRUSH_GRAIN` above (neither constant is
 * re-exported from the package's public `index.ts`). Both grain axes use the
 * same 0..2 index (0=細 finest, 1=中 mid, 2=粗 coarsest per the kernel's
 * pre-baked map ordering), so a single default value covers both pens.
 */
export const DEFAULT_CRAYON_GRAIN = 0;
export const DEFAULT_PASTEL_GRAIN = 1;

/**
 * Generate a fresh per-stroke airbrush seed (airbrush-design-2026-08-15.md
 * §4-4 / §5-3). MUST be cryptographically random and MUST NOT be derived from
 * `Date.now()`, a monotonic counter, or any per-user sequence — the design
 * doc calls this out explicitly: two clients who start airbrush strokes in
 * the same millisecond would otherwise draw with the same seed, correlating
 * their grain patterns in a way that "looks unnatural" even though it isn't a
 * hard correctness break. `crypto.getRandomValues` over the u32 space makes
 * collisions negligible.
 *
 * Call this exactly once per stroke (`DrawingCanvas.beginStrokeNow`), not once
 * per `penParamsFor` call — see `PenConfig.airbrushSeed` doc for why.
 */
export const generateAirbrushSeed = (): number => {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return arr[0] ?? 0;
};

/**
 * Derive the per-stroke `penParams` (texture-brush-design.md §4 案B / §11
 * step 3 / §12.2 / airbrush-design-2026-08-15.md §4) from the local
 * `PenConfig`.
 * - `pen === 'texture'` → `{ kind:'texture', preset }`.
 * - `pen === 'tone'` → `{ kind:'tone', pattern:'dot', density, pitch }`
 *   (段階1: dot halftone + 連続番手スカラ、封印解除時に pitch を追加
 *   phase11-light-pens-2026-08-21.md §4.6: UI は常に pitch を明示的に載せる).
 * - `pen === 'airbrush'` → `{ kind:'airbrush', seed, grain, density }`. `seed`
 *   is read verbatim from `pen.airbrushSeed` — this function does NOT generate
 *   it (see `generateAirbrushSeed` doc: generating here would make repeated
 *   calls for the same stroke diverge, breaking the local/wire determinism
 *   invariant).
 * - `pen === 'crayon'` → `{ kind:'crayon', grain }` (phase11-light-pens-
 *   2026-08-21.md §2.6). `pen === 'pastel'` → `{ kind:'pastel', grain }` (same
 *   doc). Both `grain` fields are optional on the wire (safe render-side
 *   default), but this function always includes the current UI value so
 *   replay/late-join reproduce exactly what the artist saw.
 * - `pen === 'dot'` → `{ kind:'dot', spacing }` (same doc §3.5). `spacing` is
 *   REQUIRED on the wire (protocol `dot` arm has no optional fallback for it,
 *   unlike `crayon`/`pastel`'s `grain`), so this always includes the current
 *   UI value. `shape` is left unset (protocol reserves it for a future
 *   non-circle stamp; not exposed in UI yet, §3.5).
 * - every other pen returns `undefined` so `stroke.start.penParams` is omitted
 *   (back-compat, `oekaki-v1` preserved).
 *
 * Single source of truth for the wire shape so `DrawingCanvas`
 * (startStroke / sendStrokeStart / lead) and any future caller agree.
 */
export const penParamsFor = (pen: PenConfig): PenParams | undefined => {
  if (pen.pen === 'texture') return { kind: 'texture', preset: pen.texturePreset };
  if (pen.pen === 'tone') {
    return {
      kind: 'tone',
      pattern: 'dot',
      density: pen.toneDensity,
      pitch: pen.tonePitch,
    };
  }
  if (pen.pen === 'dot') return { kind: 'dot', spacing: pen.dotSpacing };
  if (pen.pen === 'airbrush') {
    return {
      kind: 'airbrush',
      seed: pen.airbrushSeed,
      grain: pen.airbrushGrain,
      density: pen.airbrushDensity,
    };
  }
  if (pen.pen === 'crayon') return { kind: 'crayon', grain: pen.crayonGrain };
  if (pen.pen === 'pastel') return { kind: 'pastel', grain: pen.pastelGrain };
  return undefined;
};

/** Default pen config used on first room entry. */
export const DEFAULT_PEN_CONFIG: PenConfig = {
  pen: 'brush',
  color: 0x000000,
  alpha: 255,
  // 2026-05-16 fb: `DEFAULT_PEN_WIDTHS[0] = 1` で UI の cell 0 が default 選択
  // 状態なのに pen.width が 4 で初期化されていて「UI 上は 1px 選択なのに描くと
  // 4px」のズレがあった. cell 0 の値 (= 1) に揃える.
  // penWidthSlider.ts の DEFAULT_PEN_WIDTHS[0] と一致させる前提.
  width: 1,
  mask: { type: 'none', colors: [] },
  layerId: 0,
  // §9.9 既定は安全側 (= AA 無効、wire byte-identical)。実効値 (tri-state、
  // §1.1) は chrome (useRoomChrome) が aaPreference から pen.aa に同期する。
  aa: false,
  // texture preset の既定は紙 (DEFAULT_TEXTURE_PRESET と一致、texture-brush-design.md
  // §10.1)。pen='texture' でないときは無視される (penParamsFor が undefined を返す)。
  texturePreset: 'paper',
  // トーン番手の既定 (texture-brush-design.md §12.2、drawing-core kernel 既定と一致)。
  // pen='tone' でないときは無視される (penParamsFor が undefined を返す)。
  toneDensity: DEFAULT_TONE_DENSITY,
  // トーン間隔 (pitch) の既定 (phase11-light-pens-2026-08-21.md §4.5、UI 既定 16)。
  // pen='tone' でないときは無視される (penParamsFor が undefined を返す)。
  tonePitch: DEFAULT_TONE_PITCH,
  // エアブラシ grain/density の既定 (airbrush-design-2026-08-15.md §1.3、drawing-core
  // kernel 既定と一致)。pen='airbrush' でないときは無視される (penParamsFor が
  // undefined を返す)。
  airbrushGrain: DEFAULT_AIRBRUSH_GRAIN,
  airbrushDensity: DEFAULT_AIRBRUSH_DENSITY,
  // 常に上書きされる placeholder (= PenConfig.airbrushSeed の doc 参照)。initial
  // render で airbrush が選ばれることは無い (既定 pen='brush') ので実際に wire へ
  // 乗ることはない。
  airbrushSeed: 0,
  // クレヨン/パステル grain の既定 (phase11-light-pens-2026-08-21.md §2.6、drawing-core
  // kernel 既定と一致)。pen が該当ペンでないときは無視される (penParamsFor が undefined
  // を返す)。
  crayonGrain: DEFAULT_CRAYON_GRAIN,
  pastelGrain: DEFAULT_PASTEL_GRAIN,
  // ドット間隔 (spacing) の既定 (phase11-light-pens-2026-08-21.md §3.4/§3.5、drawing-core
  // kernel 既定 DOT_DEFAULT_SPACING と一致)。pen='dot' でないときは無視される
  // (penParamsFor が undefined を返す)。
  dotSpacing: DEFAULT_DOT_SPACING,
};
